export interface AppEmbedAuthContext {
  token: string;
  accountId: string;
  accountLabel: string;
  apiBase: string;
}

const AUTH_MODE = 'postMessage';
const MSG_AUTH = 'auth';
const MSG_READY = 'ready';
const MSG_VERSION = 1;

/** Tiempo de espera al JWT desde la app contenedora (refresh de sesión en red lenta). */
const DEFAULT_PARENT_AUTH_TIMEOUT_MS = 45_000;

function readQueryContext(defaultApiBase: string): {
  accountId: string;
  accountLabel: string;
  apiBase: string;
  authMode: string;
  parentOrigin: string;
  urlToken: string;
  embed: boolean;
  /** Secreto compartido (query `embedToken`) para saldo embed sin JWT de la app. */
  embedToken: string;
} {
  const params = new URLSearchParams(window.location.search);
  return {
    accountId: params.get('accountId') || '',
    accountLabel: params.get('accountLabel') || '',
    apiBase: (params.get('apiBase') || defaultApiBase).replace(/\/$/, ''),
    authMode: params.get('authMode') || '',
    parentOrigin: params.get('parentOrigin') || '',
    urlToken: params.get('token') || '',
    embed: params.get('embed') === '1',
    embedToken: params.get('embedToken') || '',
  };
}

/** Orígenes típicos del shell Ionic/Capacitor (pueden diferir del `parentOrigin` en query). */
function isKnownAppShellOrigin(origin: string): boolean {
  if (!origin) return false;
  return (
    origin.startsWith('capacitor://') ||
    origin.startsWith('ionic://') ||
    origin === 'http://localhost:8100' ||
    origin === 'http://localhost:4200' ||
    origin === 'http://localhost' ||
    origin === 'https://localhost'
  );
}

function isEmbedInAppShell(): boolean {
  const q = readQueryContext('');
  return q.embed && window.parent !== window;
}

function isAllowedParentOrigin(origin: string, expected: string): boolean {
  const normalized = String(origin ?? '').trim();
  if (!normalized) {
    return isEmbedInAppShell();
  }
  if (expected && normalized === expected) return true;
  if (expected && isKnownAppShellOrigin(normalized) && isKnownAppShellOrigin(expected)) {
    return true;
  }
  if (isEmbedInAppShell()) {
    if (isKnownAppShellOrigin(normalized)) return true;
    if (normalized === 'null') return true;
    if (!expected) return true;
  }
  if (!expected && window.parent !== window) {
    return isKnownAppShellOrigin(normalized);
  }
  return false;
}

function shouldWaitForParentPostMessage(q: ReturnType<typeof readQueryContext>): boolean {
  if (!q.accountId) return false;
  if (q.authMode === AUTH_MODE) return true;
  return q.embed && window.parent !== window;
}

/** Avisar a la app contenedora apenas carga el iframe (antes de auth y saldo). */
export function pingAppEmbedParentForAuth(defaultApiBase = ''): void {
  const q = readQueryContext(defaultApiBase);
  if (!shouldWaitForParentPostMessage(q)) return;
  notifyParentReady(q.parentOrigin);
}

function notifyParentReady(parentOrigin: string): void {
  const payload = JSON.stringify({ code: MSG_READY });
  try {
    window.parent.postMessage(payload, '*');
  } catch {
    /* ignore */
  }
  if (parentOrigin && parentOrigin !== '*') {
    try {
      window.parent.postMessage(payload, parentOrigin);
    } catch {
      /* ignore */
    }
  }
}

function parseParentAuthPayload(data: unknown): {
  token: string;
  accountId: string;
  accountLabel: string;
  apiBase: string;
} | null {
  try {
    const raw = typeof data === 'string' ? JSON.parse(data) : data;
    if (!raw || typeof raw !== 'object') return null;
    const row = raw as Record<string, unknown>;
    if (row.code !== MSG_AUTH && row.code != null) return null;
    const version = row.version;
    if (version != null && Number(version) !== MSG_VERSION) return null;
    const token = String(row.token ?? row.accessToken ?? '').trim();
    if (!token) return null;
    return {
      token,
      accountId: String(row.accountId ?? '').trim(),
      accountLabel: String(row.accountLabel ?? '').trim(),
      apiBase: String(row.apiBase ?? '').replace(/\/$/, ''),
    };
  } catch {
    return null;
  }
}

function waitForParentAuth(
  parentOrigin: string,
  timeoutMs: number,
  expectedAccountId: string
): Promise<AppEmbedAuthContext | null> {
  return new Promise((resolve) => {
    if (window.parent === window) {
      resolve(null);
      return;
    }

    let settled = false;
    let readyInterval = 0;
    let readySlowInterval = 0;
    const finish = (value: AppEmbedAuthContext | null) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      if (readyInterval) window.clearInterval(readyInterval);
      if (readySlowInterval) window.clearInterval(readySlowInterval);
      resolve(value);
    };

    const onMessage = (event: MessageEvent) => {
      const parsed = parseParentAuthPayload(event.data);
      if (!parsed) return;

      const originOk = isAllowedParentOrigin(event.origin, parentOrigin);
      const embedTrust = isEmbedInAppShell() && Boolean(parsed.token);
      if (!originOk && !embedTrust) return;

      const accountId = parsed.accountId;
      if (expectedAccountId && accountId && accountId !== expectedAccountId) return;

      finish({
        token: parsed.token,
        accountId: accountId || expectedAccountId,
        accountLabel: parsed.accountLabel,
        apiBase: parsed.apiBase,
      });
    };

    window.addEventListener('message', onMessage);

    notifyParentReady(parentOrigin);
    readyInterval = window.setInterval(() => notifyParentReady(parentOrigin), 250);
    readySlowInterval = window.setInterval(() => notifyParentReady(parentOrigin), 1500);

    const timer = window.setTimeout(() => finish(null), timeoutMs);
  });
}

/**
 * Token legacy en query (`token=`) o entrega segura vía postMessage desde la app contenedora.
 */
export function readEmbedPageQuery(defaultApiBase: string): {
  accountId: string;
  accountLabel: string;
  apiBase: string;
  embed: boolean;
  embedToken: string;
} {
  const q = readQueryContext(defaultApiBase);
  return {
    accountId: q.accountId,
    accountLabel: q.accountLabel,
    apiBase: q.apiBase,
    embed: q.embed,
    embedToken: q.embedToken,
  };
}

export async function resolveAppEmbedAuthContext(
  defaultApiBase: string,
  options?: { timeoutMs?: number }
): Promise<AppEmbedAuthContext | null> {
  const q = readQueryContext(defaultApiBase);
  if (q.urlToken && q.accountId) {
    return {
      token: q.urlToken,
      accountId: q.accountId,
      accountLabel: q.accountLabel,
      apiBase: q.apiBase,
    };
  }

  if (shouldWaitForParentPostMessage(q)) {
    const timeoutMs = options?.timeoutMs ?? DEFAULT_PARENT_AUTH_TIMEOUT_MS;
    const fromParent = await waitForParentAuth(q.parentOrigin, timeoutMs, q.accountId);
    if (fromParent?.token) {
      return {
        token: fromParent.token,
        accountId: fromParent.accountId || q.accountId,
        accountLabel: fromParent.accountLabel || q.accountLabel,
        apiBase: fromParent.apiBase || q.apiBase,
      };
    }
  }

  return null;
}
